import { type ReactNode, useRef, useState } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover'
import {
  CalendarDays,
  FileText,
  FileTextIcon,
  Github,
  ImageIcon,
  LinkIcon,
  Loader2Icon,
  PaletteIcon,
  Plug,
  StickyNoteIcon,
  TypeIcon,
  XIcon,
} from 'lucide-react'
import type { ConnectorProvider } from './item-defaults'

// Non-image file types accepted by the "File" button. Images get their own
// button (accept="image/*"), so this deliberately omits image/*.
const FILE_ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/json,application/javascript,application/xml,text/*,audio/*,video/*,font/*,.ttf,.otf,.woff,.woff2'

// Fonts get their own button for discoverability — the File button technically
// accepts them, but nobody guesses that. This one takes ONLY font files.
const FONT_ACCEPT = 'font/ttf,font/otf,font/woff,font/woff2,.ttf,.otf,.woff,.woff2'

// The canvas's "add" surface. Pure dispatcher — it doesn't know about positions or
// item shapes. The detail page receives raw inputs and handles upload + persist so
// it can wrap them in optimistic pending-card state.
// The buttons no longer collect content in a modal — they drop an editable card straight
// onto the canvas (a darkened modal made it hard to reference the board while typing/picking).
// The detail page turns each signal into a draft card (text/link) or a placed card (color).
type Props = {
  onAddText: (kind: 'reference' | 'instruction') => void
  onAddUrl: () => void
  onAddColor: () => Promise<void> | void
  onUploadFiles: (files: File[]) => Promise<void> | void
  onAddConnector: (provider: ConnectorProvider) => void
  // Optional action pinned to the right end of the toolbar, after a divider. The
  // moodboard's Analyze / Regenerate CTA lives here.
  trailing?: ReactNode
}

export function AddItemToolbar({
  onAddText,
  onAddUrl,
  onAddColor,
  onUploadFiles,
  onAddConnector,
  trailing,
}: Props) {
  return (
    // Centering is based on the add-items pill alone: the trailing cluster
    // (Analyze CTA + Brief) is absolutely positioned off the pill's right
    // edge so it doesn't shift the pill off-center.
    // Below lg the pill drops to its own row and stacks the trailing cluster under
    // it: in the top row it covered the name pill and the onboarding Finalize CTA.
    // Labels only fit from xl; below that the buttons are icon-only.
    <div className="absolute left-1/2 top-20 z-10 flex w-fit -translate-x-1/2 flex-col items-center gap-2 lg:top-6 lg:block">
      <div className="bg-card/95 flex max-w-[calc(100vw-2rem)] gap-1 overflow-x-auto rounded-full p-1 shadow-lg backdrop-blur">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          aria-label="Note"
          title="Note"
          onClick={() => onAddText('reference')}>
          <StickyNoteIcon size={14} />
          <span className="hidden xl:inline">Note</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          aria-label="Instruction"
          title="Instruction"
          onClick={() => onAddText('instruction')}>
          <XIcon size={14} />
          <span className="hidden xl:inline">Instruction</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          aria-label="Link"
          title="Link"
          onClick={() => onAddUrl()}>
          <LinkIcon size={14} />
          <span className="hidden xl:inline">Link</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          aria-label="Color"
          title="Color"
          onClick={() => void onAddColor()}>
          <PaletteIcon size={14} />
          <span className="hidden xl:inline">Color</span>
        </Button>
        <UploadButton
          label="Image"
          icon={<ImageIcon size={14} />}
          accept="image/*"
          onUploadFiles={onUploadFiles}
        />
        <UploadButton
          label="File"
          icon={<FileTextIcon size={14} />}
          accept={FILE_ACCEPT}
          onUploadFiles={onUploadFiles}
        />
        <UploadButton
          label="Font"
          icon={<TypeIcon size={14} />}
          accept={FONT_ACCEPT}
          onUploadFiles={onUploadFiles}
        />
        <ConnectorMenuButton onAddConnector={onAddConnector} />
      </div>
      {trailing && (
        <div className="lg:absolute lg:left-full lg:top-1/2 lg:ml-2 lg:-translate-y-1/2">
          {trailing}
        </div>
      )}
    </div>
  )
}

// One "Connect" button that opens a provider chooser; picking a provider drops the connector
// draft card (which then shows the target picker).
function ConnectorMenuButton({ onAddConnector }: { onAddConnector: Props['onAddConnector'] }) {
  const [open, setOpen] = useState(false)
  const pick = (provider: ConnectorProvider) => {
    setOpen(false)
    onAddConnector(provider)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 rounded-full"
          aria-label="Connect"
          title="Connect">
          <Plug size={14} />
          <span className="hidden xl:inline">Connect</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-44 p-1">
        <button
          type="button"
          onClick={() => pick('github')}
          className="hover:bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm">
          <Github size={14} />
          GitHub
        </button>
        <button
          type="button"
          onClick={() => pick('notion')}
          className="hover:bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm">
          <FileText size={14} />
          Notion
        </button>
        <button
          type="button"
          onClick={() => pick('google_calendar')}
          className="hover:bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm">
          <CalendarDays size={14} />
          Google Calendar
        </button>
      </PopoverContent>
    </Popover>
  )
}

function UploadButton({
  label,
  icon,
  accept,
  onUploadFiles,
}: {
  label: string
  icon: ReactNode
  accept: string
  onUploadFiles: Props['onUploadFiles']
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFiles = async (files: File[]) => {
    setUploading(true)
    try {
      await onUploadFiles(files)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept={accept}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) void handleFiles(files)
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 rounded-full"
        aria-label={label}
        title={label}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}>
        {uploading ? <Loader2Icon size={14} className="animate-spin" /> : icon}
        <span className="hidden xl:inline">{uploading ? 'Uploading…' : label}</span>
      </Button>
    </>
  )
}
